import { httpDelete, httpGet, httpPatch, httpPost } from "../connection";
import type {
  ActivityLogBody,
  AssetActivityLogResult,
  ConnectionFailurePayload,
  CreateLabAssetBody,
  LabAsset,
  LabAssetMeta,
  LabAssetType,
  QueryLabAssetsBody,
  QueryLabAssetsResult,
  UpdateLabAssetBody,
} from "./lab-assets.types";

// The metadata routes (vendors/models/names/locations/meta) strictly require
// asset_type — monolith-api rejects the request with a 400 without it.
export type AssetType = LabAssetType;

export type AssetTypeQuery = {
  asset_type: AssetType;
};

export async function getAssetItems(
  params: QueryLabAssetsBody = {}
): Promise<QueryLabAssetsResult> {
  return await httpGet(`/assets`, params);
}

export async function queryAssets(
  params: QueryLabAssetsBody
): Promise<QueryLabAssetsResult> {
  return await httpPost(`/assets/query`, params);
}

// GET /assets/:uuid returns a single bare AssetDbRecord (not a paged
// envelope); 404 if no match. The list endpoints' strict schemas reject a
// `uuid` key, so this is the only uuid-based fetch. A failure (404 included)
// resolves as { success: false } via connection.js instead of rejecting, so
// callers must runtime-check the payload shape.
export async function getAssetItem(
  asset_uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<LabAsset> {
  return await httpGet(`/assets/${asset_uuid}`, {}, options);
}

export async function createAssetItem(
  values: CreateLabAssetBody,
  options: { skipErrorToast?: boolean } = {}
) {
  return await httpPost(`/assets`, values, options);
}

// PATCH /assets/:uuid merges only the keys present (see UpdateLabAssetBody).
// Like the other connection.js helpers it resolves { success: false, error }
// on HTTP errors instead of rejecting, so callers must check the result.
export async function updateAssetItem(
  asset_uuid: string,
  values: UpdateLabAssetBody,
  options: { skipErrorToast?: boolean } = {}
) {
  return await httpPatch(`/assets/${asset_uuid}`, values, options);
}

export async function deleteAssetItem(
  asset_uuid: string,
  options: { skipErrorToast?: boolean } = {}
) {
  return await httpDelete(`/assets/${asset_uuid}`, undefined, options);
}

export async function exportAssetList(params: any) {
  return await httpPost(`/assets/export`, params);
}

export async function getAssetVendors(params: AssetTypeQuery) {
  return await httpGet(`/assets/vendors`, params);
}

export async function getAssetModels(params: AssetTypeQuery) {
  return await httpGet(`/assets/models`, params);
}

export async function getAssetLocations(params: AssetTypeQuery) {
  return await httpGet(`/assets/locations`, params);
}

export async function getAssetNames(params: AssetTypeQuery) {
  return await httpGet(`/assets/names`, params);
}

// Callers must narrow before reading the lists (see ConnectionFailurePayload).
export async function getAssetMeta(
  params: AssetTypeQuery
): Promise<LabAssetMeta | ConnectionFailurePayload> {
  return await httpGet(`/assets/meta`, params);
}

export async function getAssetVersions(params = {}) {
  return await httpGet(`/assets/versions`, params);
}

// GET /assets/format returns a bare array of format rows and PATCH takes an
// ordered list of { type, value } sections, where a Number section sends
// [format_mod, iterator, iterator]. Same contract as /storage/format.
export type AssetNumberFormatRow = {
  format_id?: number;
  format_mod?: string | number;
  format_section: string;
  initial_text?: string;
  iterator?: number | string;
  restart?: number;
  starting_value?: number;
};

export type AssetNumberFormatSection = {
  type: string;
  value: string | number | Array<string | number | undefined> | undefined;
};

export async function getAssetNumberFormat(): Promise<AssetNumberFormatRow[]> {
  return await httpGet(`/assets/format`);
}

export async function updateAssetNumberFormat(
  values: AssetNumberFormatSection[]
) {
  return await httpPatch(`/assets/format`, values);
}

export async function getAssetActivityLog(
  asset_uuid: string,
  params: ActivityLogBody = {}
): Promise<AssetActivityLogResult | ConnectionFailurePayload> {
  return await httpPost(`/assets/${asset_uuid}/activity-log`, params);
}

const LabAssetsAPI = {
  getAssetItems,
  getAssetItem,
  queryAssets,
  createAssetItem,
  updateAssetItem,
  deleteAssetItem,
  exportAssetList,
  getAssetVendors,
  getAssetModels,
  getAssetLocations,
  getAssetNames,
  getAssetMeta,
  getAssetVersions,
  getAssetActivityLog,
  getAssetNumberFormat,
  updateAssetNumberFormat,
};

export default LabAssetsAPI;
